/**
 * Notification Analytics
 * 
 * Track notification performance and user engagement
 */

import { analytics } from './analytics';

export interface NotificationEvent {
  type: 'delivered' | 'clicked' | 'dismissed' | 'enabled' | 'disabled' | 'time_changed';
  notificationType?: string;
  timestamp: number;
  metadata?: Record<string, any>;
}

const STORAGE_KEY = 'tension_notification_analytics';

/**
 * Track notification event
 */
export function trackNotificationEvent(event: NotificationEvent): void {
  // Track in main analytics
  analytics.track(`notification_${event.type}`, {
    notification_type: event.notificationType,
    ...event.metadata,
  });

  // Store in local analytics
  const history = getNotificationAnalytics();
  history.push(event);

  // Keep last 100 events
  if (history.length > 100) {
    history.shift();
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
}

/**
 * Get notification analytics history
 */
export function getNotificationAnalytics(): NotificationEvent[] {
  const stored = localStorage.getItem(STORAGE_KEY);
  return stored ? JSON.parse(stored) : [];
}

/**
 * Calculate notification stats
 */
export function getNotificationStats() {
  const history = getNotificationAnalytics();
  const now = Date.now();
  const last7Days = now - 7 * 24 * 60 * 60 * 1000;
  const last30Days = now - 30 * 24 * 60 * 60 * 1000;

  const recent7Days = history.filter(e => e.timestamp > last7Days);
  const recent30Days = history.filter(e => e.timestamp > last30Days);

  const delivered7Days = recent7Days.filter(e => e.type === 'delivered').length;
  const clicked7Days = recent7Days.filter(e => e.type === 'clicked').length;
  const delivered30Days = recent30Days.filter(e => e.type === 'delivered').length;
  const clicked30Days = recent30Days.filter(e => e.type === 'clicked').length;

  return {
    last7Days: {
      delivered: delivered7Days,
      clicked: clicked7Days,
      clickRate: delivered7Days > 0 ? (clicked7Days / delivered7Days) * 100 : 0,
    },
    last30Days: {
      delivered: delivered30Days,
      clicked: clicked30Days,
      clickRate: delivered30Days > 0 ? (clicked30Days / delivered30Days) * 100 : 0,
    },
    byType: getEventsByType(recent30Days),
  };
}

/**
 * Get events grouped by notification type
 */
function getEventsByType(events: NotificationEvent[]) {
  const types: Record<string, { delivered: number; clicked: number }> = {};

  events.forEach(event => {
    const type = event.notificationType || 'unknown';
    if (!types[type]) {
      types[type] = { delivered: 0, clicked: 0 };
    }

    if (event.type === 'delivered') {
      types[type].delivered++;
    } else if (event.type === 'clicked') {
      types[type].clicked++;
    }
  });

  return types;
}

/**
 * Track notification delivery
 */
export function trackNotificationDelivered(notificationType: string, metadata?: Record<string, any>): void {
  trackNotificationEvent({
    type: 'delivered',
    notificationType,
    timestamp: Date.now(),
    metadata,
  });
}

/**
 * Track notification click
 */
export function trackNotificationClicked(notificationType: string, metadata?: Record<string, any>): void {
  trackNotificationEvent({
    type: 'clicked',
    notificationType,
    timestamp: Date.now(),
    metadata,
  });
}

/**
 * Track notifications enabled
 */
export function trackNotificationsEnabled(reminderTime: string): void {
  trackNotificationEvent({
    type: 'enabled',
    timestamp: Date.now(),
    metadata: { reminderTime },
  });
}

/**
 * Track notifications disabled
 */
export function trackNotificationsDisabled(): void {
  trackNotificationEvent({
    type: 'disabled',
    timestamp: Date.now(),
  });
}

/**
 * Track reminder time change
 */
export function trackReminderTimeChanged(oldTime: string, newTime: string): void {
  trackNotificationEvent({
    type: 'time_changed',
    timestamp: Date.now(),
    metadata: { oldTime, newTime },
  });
}

/**
 * Get notification effectiveness score (0-100)
 */
export function getNotificationEffectivenessScore(): number {
  const stats = getNotificationStats();
  const clickRate = stats.last30Days.clickRate;
  const deliveryCount = stats.last30Days.delivered;

  // Score based on click rate and consistent delivery
  let score = 0;

  // Click rate component (0-60 points)
  score += Math.min(clickRate * 1.5, 60);

  // Consistency component (0-40 points)
  // Ideal: ~30 notifications per 30 days (daily)
  const consistencyScore = Math.min((deliveryCount / 30) * 40, 40);
  score += consistencyScore;

  return Math.round(score);
}
